/**
 * FK Breadcrumb container — multi-row tree visualization.
 *
 * Concern 4 of `goals/fk-breadcrumb-followup-1/GOAL.md` replaced the prior
 * "active-path + branchSlot (+N badge)" design with a tree:
 *
 *   customers ▸ #42 ─▸ addresses      (row 0, active)
 *               └────▸ orders         (row 1, sibling)
 *
 * `computeTreeLayout` produces one row per leaf-to-root path. The active
 * line is always row 0; non-active siblings render on subsequent rows.
 * Each non-spine row renders the **path from root → fork-parent** as
 * invisible chip placeholders + a chevron-width spacer so the row's first
 * VISIBLE chip lines up with the column immediately after the fork-parent
 * on row 0 (siblings stack directly under their first sibling). The visible
 * fork glyph (a combined `→ + ↘` arrow) lives on the FORK-PARENT row's
 * same-row separator slot — see `<ForkArrow />` in `FkBreadcrumbTreeRow.tsx`.
 * Activating a non-active sibling promotes that branch to row 0; the
 * previously-active branch drops to a subsequent row. `×` still dismisses
 * chip + subtree.
 *
 * Overflow: when the chip trail outgrows the breadcrumb's viewport the
 * `<nav>` scrolls horizontally (no row wrapping). Two fade+chevron edge
 * indicators surface when more chips are clipped on either side; clicking
 * one scrolls by ~60% of the visible width. On chip activation the active
 * chip smooth-scrolls to centre, clamped at the trail edges so we never
 * leave dead space before the first chip or after the last.
 *
 * Slice 5's per-tab "Show join tables" toggle still applies (filters
 * collapsed join-table chips out of all rows). Slice 6's keyboard nav
 * is unchanged.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { BranchArrowsOverlay } from './BranchArrowsOverlay';
import { FkBreadcrumbContextMenuWrapper } from './FkBreadcrumbContextMenuWrapper';
import { FkBreadcrumbTreeRow } from './FkBreadcrumbTreeRow';
import { useBreadcrumbKeyboard } from './useBreadcrumbKeyboard';
import {
  activePathFromRoot,
  collectSubtree,
} from './breadcrumbTree';
import {
  computeForkAnchors,
  computeTreeLayout,
  filterJoinCollapsedRows,
} from './breadcrumbTreeLayout';
import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
} from '../../../core/types/workspaceTypes';
import type { JoinTableHint } from '../../../single-row/utils/joinTableHintTypes';

type FkBreadcrumbProps = {
  readonly tabId: string;
  readonly tree: NavigationTree;
  readonly onActivate: (id: NavigationNodeId) => void;
  readonly onDismiss: (id: NavigationNodeId) => void;
  readonly onSetJoinTableHint?: (args: {
    schema: string;
    table: string;
    hint: JoinTableHint | null;
  }) => void;
};

/** How much of the visible width an indicator click scrolls by. */
const INDICATOR_SCROLL_FRACTION = 0.6;
/** Px tolerance for "fully scrolled to start/end" — avoid flickery indicators. */
const SCROLL_EDGE_EPSILON = 1;

export function FkBreadcrumb({
  tabId,
  tree,
  onActivate,
  onDismiss,
  onSetJoinTableHint,
}: FkBreadcrumbProps) {
  const { track } = useAnalytics();
  const [showJoinTables, setShowJoinTables] = useState(false);

  // Local optimistic active id. Keeping this in `FkBreadcrumb` (a small
  // subtree) instead of lifting it to `TableExplorerTab` is the whole point:
  // setting it triggers a render of THIS component only, not the entire
  // table-explorer tree. Combined with the `requestAnimationFrame` deferral
  // of the parent `onActivate` below, the user sees the chip flip to the amber
  // pill on the very next paint — before the cascade re-render that swaps the
  // data view fires. (Breadcrumb navigation never refetches: the target
  // node's data was already loaded when the chip first appeared, so the
  // 200ms lag the user felt was React render cost, not data prep — no spinner.)
  const [pendingActivateId, setPendingActivateId] = useState<NavigationNodeId | null>(null);

  useEffect(() => {
    if (pendingActivateId && tree.activeNodeId === pendingActivateId) {
      setPendingActivateId(null);
    }
  }, [tree.activeNodeId, pendingActivateId]);

  useBreadcrumbKeyboard({ tree, onActivate });

  const layout = useMemo(() => computeTreeLayout(tree), [tree]);
  const activePath: NavigationNode[] = useMemo(
    () => activePathFromRoot(tree),
    [tree],
  );

  const scrollRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const recomputeScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft;
    setCanScrollLeft(left > SCROLL_EDGE_EPSILON);
    setCanScrollRight(left < maxScroll - SCROLL_EDGE_EPSILON);
  }, []);

  // Re-evaluate scroll affordances when the tree, viewport, or content size
  // changes. ResizeObserver covers both viewport width changes (window resize,
  // side-panel toggle) and content width changes (chip add/remove/dismiss).
  useLayoutEffect(() => {
    recomputeScrollState();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(recomputeScrollState);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [recomputeScrollState, tree]);

  // On active-chip change, smooth-scroll it toward the visible centre. Clamp
  // the target so we never reveal empty space past either content edge. The
  // effect keys on the OPTIMISTIC id (`pendingActivateId ?? tree.activeNodeId`)
  // so the scroll animation kicks off the same frame the chip flips to active
  // styling — otherwise the chip would move first and the scroll catch up one
  // heavy-render later.
  const displayedActiveId = pendingActivateId ?? tree.activeNodeId;
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const activeChip =
      container.querySelector<HTMLElement>('[data-pending-activate="true"]') ??
      container.querySelector<HTMLElement>('[data-active="true"]');
    if (!activeChip) return;
    const containerRect = container.getBoundingClientRect();
    const chipRect = activeChip.getBoundingClientRect();
    const chipCenterInContent =
      chipRect.left - containerRect.left + container.scrollLeft + chipRect.width / 2;
    const target = chipCenterInContent - container.clientWidth / 2;
    const clamped = Math.max(
      0,
      Math.min(target, container.scrollWidth - container.clientWidth),
    );
    container.scrollTo({ left: clamped, behavior: 'smooth' });
  }, [displayedActiveId]);

  const scrollByFraction = useCallback((direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = el.clientWidth * INDICATOR_SCROLL_FRACTION * direction;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const totalNodeCount = Object.keys(tree.nodes).length;
  if (totalNodeCount <= 1) return null;

  const previousActive = tree.activeNodeId;

  // Apply the join-collapse filter uniformly across all rows. If "Show join
  // tables" is off, hide collapsed table-nodes from the layout output, then
  // drop any row that emptied out as a result.
  const renderableRows = filterJoinCollapsedRows(layout, tree, showJoinTables)
    .filter((r) => r.row.length > 0);

  // Fork anchors describe the down-branch arrows the overlay draws for
  // each fork-parent. Same-row arrows are NOT drawn by the overlay — the
  // inline chevron renders them uniformly regardless of branching, so the
  // visual is consistent whether a chip has off-spine children or not.
  const forkAnchors = computeForkAnchors(renderableRows);

  const fireChipClick = (id: NavigationNodeId, node: NavigationNode) => {
    const prevIdx = activePath.findIndex((n) => n.id === previousActive);
    const nextIdx = activePath.findIndex((n) => n.id === id);
    const distance =
      prevIdx >= 0 && nextIdx >= 0 ? Math.abs(prevIdx - nextIdx) : 1;
    track('feature:fk_nav_chip_click', {
      nodeKind: node.kind,
      distance,
      via: 'chip',
    });
  };

  const handleActivate = (id: NavigationNodeId) => {
    if (id === previousActive) return;
    const node = tree.nodes[id];
    if (!node) return;
    fireChipClick(id, node);
    // 1) Flip the optimistic state synchronously → React commits a tiny
    //    re-render of `FkBreadcrumb` (this component only) → paint shows the
    //    clicked chip in its amber pill.
    // 2) Defer the parent `onActivate` (which triggers the heavy
    //    `TableExplorerTab` cascade re-render) to the next animation frame so
    //    step 1's paint isn't blocked by it.
    setPendingActivateId(id);
    requestAnimationFrame(() => onActivate(id));
  };

  const handleDismiss = (id: NavigationNodeId) => {
    const node = tree.nodes[id];
    if (!node) return;
    const subtreeSize = collectSubtree(tree, id).length;
    track('feature:fk_nav_chip_dismiss', {
      nodeKind: node.kind,
      subtreeSize,
    });
    // Same rAF guard for dismiss — the chip's × press fires its CSS :active
    // pseudo-class instantly; deferring the store mutation by one frame keeps
    // the visual press distinct from the cascade re-render.
    requestAnimationFrame(() => onDismiss(id));
  };

  return (
    <FkBreadcrumbContextMenuWrapper
      showJoinTables={showJoinTables}
      onToggleShowJoinTables={() => setShowJoinTables((v) => !v)}
    >
      <div className="relative flex-shrink-0 border-b border-border bg-background/60">
        <nav
          ref={scrollRef}
          aria-label="Foreign-key navigation breadcrumb"
          className="fk-breadcrumb-scroll overflow-x-auto px-2 py-1"
          data-testid="fk-breadcrumb"
          data-tab-id={tabId}
          data-depth={renderableRows[0]?.row.length ?? 0}
          data-row-count={renderableRows.length}
          data-show-join-tables={showJoinTables ? 'true' : 'false'}
          data-can-scroll-left={canScrollLeft ? 'true' : 'false'}
          data-can-scroll-right={canScrollRight ? 'true' : 'false'}
          onScroll={recomputeScrollState}
        >
          <div
            ref={contentRef}
            className="relative flex flex-col gap-0.5 w-fit min-w-full"
          >
            {renderableRows.map((row, idx) => (
              <FkBreadcrumbTreeRow
                key={`row-${idx}-${row.row[0] ?? 'empty'}`}
                tree={tree}
                row={row}
                // Spine carries no leading connector slot; every off-spine
                // row does. The visual stacking order ("deepest fork first")
                // is computed in `computeTreeLayout`.
                hasConnector={!row.isSpine}
                pendingActivateId={pendingActivateId}
                onActivate={handleActivate}
                onDismiss={handleDismiss}
                onSetJoinTableHint={onSetJoinTableHint}
              />
            ))}
            <BranchArrowsOverlay
              scrollContainerRef={scrollRef}
              contentContainerRef={contentRef}
              rows={renderableRows}
              anchors={forkAnchors}
            />
          </div>
        </nav>
        <ScrollEdgeIndicator
          side="left"
          visible={canScrollLeft}
          onClick={() => scrollByFraction(-1)}
        />
        <ScrollEdgeIndicator
          side="right"
          visible={canScrollRight}
          onClick={() => scrollByFraction(1)}
        />
      </div>
    </FkBreadcrumbContextMenuWrapper>
  );
}

type ScrollEdgeIndicatorProps = {
  readonly side: 'left' | 'right';
  readonly visible: boolean;
  readonly onClick: () => void;
};

function ScrollEdgeIndicator({ side, visible, onClick }: ScrollEdgeIndicatorProps) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  const placement = side === 'left' ? 'left-0' : 'right-0';
  const gradient =
    side === 'left'
      ? 'bg-gradient-to-r from-background to-transparent justify-start pl-1'
      : 'bg-gradient-to-l from-background to-transparent justify-end pr-1';
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Scroll breadcrumb left' : 'Scroll breadcrumb right'}
      title={side === 'left' ? 'Scroll left' : 'Scroll right'}
      data-testid={`fk-breadcrumb-scroll-${side}`}
      onClick={onClick}
      tabIndex={visible ? 0 : -1}
      aria-hidden={visible ? undefined : true}
      className={`absolute top-0 bottom-0 ${placement} w-9 flex items-center ${gradient} transition-opacity duration-150 ease-out ${
        visible
          ? 'opacity-100 pointer-events-auto cursor-pointer'
          : 'opacity-0 pointer-events-none'
      }`}
    >
      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-background/85 border border-border text-muted-foreground shadow-sm hover:text-foreground hover:bg-background">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    </button>
  );
}
